import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { PostTitleFormData } from '../../forms/PostTitleForm';
import {
  PostTitleForm,
  PostTitlePinnedSchema,
  PostTitleSchema,
} from '../../forms/PostTitleForm';

const ADD_TITLE_TO_POST_MUTATION = graphql(`
  mutation AddTitleToPost($data: AddTitleToPostInput) {
    addTitleToPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

const POST_EDIT_TITLE_DIALOG = graphql(`
  fragment postEditTitleDialog on Post {
    _id
    title
    pinned
  }
`);

interface Props {
  post: DocumentType<typeof POST_EDIT_TITLE_DIALOG>;
  open: boolean;
  onClose: () => void;
}

export const PostEditTitleDialog = ({ post, open, onClose }: Props) => {
  const { _id: postId, pinned } = post;

  const { control, handleSubmit } = useForm<PostTitleFormData>({
    values: {
      title: post.title ?? '',
    },
    resolver: zodResolver(pinned ? PostTitlePinnedSchema : PostTitleSchema),
  });

  const [addTitleToPost, { loading }] = useMutation(
    ADD_TITLE_TO_POST_MUTATION,
    {
      completionMessage: `Title ${post.title ? 'added to' : 'edited on'} post`,
      onCompleted: onClose,
    },
  );

  const onSubmit = (data: PostTitleFormData) => {
    addTitleToPost({
      variables: {
        data: {
          post: postId,
          title: data.title,
        },
      },
    });
  };

  return (
    <Or2Dialog
      keepMounted
      maxWidth="sm"
      loading={loading}
      open={open}
      onClose={onClose}
      title={`${post.title ? 'Edit' : 'Add'} title`}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Close
          </Or2Button>
          <Or2Button disabled={loading} onClick={handleSubmit(onSubmit)}>
            Save
          </Or2Button>
        </>
      }
    >
      <PostTitleForm control={control} />
    </Or2Dialog>
  );
};
